// @flow
import classnames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import AppBar from '@material-ui/core/AppBar';
import Hidden from '@material-ui/core/Hidden';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';

import ProfileMenu from '../Profile/ProfileMenu';

import { BackendBadge, getBadge } from '../../modules/scribe/BackendBadge';

import { handleNewRoomStatus } from '../../actions/conversation';
import { clearConversationStatus, setRateConversationOpen } from '../../actions/rate-conversation';
import { setSelectedTranscriptId } from '../../actions/saved-transcript';
import { fetchAvaProfileMetrics } from '../../actions/session';

import { SavedTranscript } from '../../types/saved-transcript';

import { trackConversationRating } from '../../utils/conversation';

const useStyles = makeStyles(theme => ({
  appBar: {
    right: 0,
    WebkitAppRegion: 'no-drag',
    paddingTop: 6,
    paddingRight: 8,
    paddingBottom: 6,
    paddingLeft: 30,
    background: theme.palette.type === 'light' ? theme.palette.ava.white : theme.palette.ava.dark2,
    boxShadow:
      theme.palette.type === 'light'
        ? '0px 4px 4px rgba(112, 144, 176, 0.25)'
        : '0px 4px 4px rgba(112, 144, 176, 0.04)',
    opacity: 0.98,
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 0,
    },
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.type === 'light' ? theme.palette.ava.dark1 : theme.palette.ava.white,
    fontSize: 26,
    lineHeight: '41.6px',
    [theme.breakpoints.down('xs')]: {
      marginRight: 16,
    },
  },
  arrowBack: {
    cursor: 'pointer',
  },
}));

interface Props {
  backends: Object;
  className?: string;
  conversationStatus: Object;
  currentPlatform: string;
  displayable: Object;
  features: Object;
  fetchAvaProfileMetrics: Function;
  history: Object;
  isRateConversationOpen: boolean;
  selectedTranscriptId: string;
  style?: Object;
  transcripts: SavedTranscript[];
  handleNewRoomStatus: Function;
  setRateConversationOpen: Function;
  setSelectedTranscriptId: Function;
  user: Object;
}

const HomeView = ({
  backends,
  className,
  conversationStatus,
  displayable,
  features,
  fetchAvaProfileMetrics,
  handleNewRoomStatus,
  history,
  isRateConversationOpen,
  selectedTranscriptId,
  setRateConversationOpen,
  setSelectedTranscriptId,
  style,
  transcripts,
  user,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    location: { pathname },
  } = history;

  const getHeadingText = () => {
    if (pathname.includes('/web/conversations')) return t('sidebar.conversations');
    if (pathname.includes('/web/account-settings')) return t('sidebar.accountSettings');
    if (pathname.includes('/web/scribe-dashboard')) return t('sidebar.scribeDashboard');
    return '';
  };

  const onCloseSelectedTranscript = () => {
    handleNewRoomStatus({}, true);
    setSelectedTranscriptId('');

    if (isRateConversationOpen) {
      setRateConversationOpen(false);
      trackConversationRating({
        fetchAvaProfileMetrics,
        rate: 0,
        status: conversationStatus,
        user,
      });
      clearConversationStatus();
    }
  };

  const selectedTranscript = transcripts.find(transcript => transcript.roomId === selectedTranscriptId);

  const backend = getBadge(backends);

  return (
    <AppBar
      className={classnames(classes.appBar, className)}
      position="fixed"
      color="inherit"
      style={{
        WebKitAppRegion: 'no-drag',
        ...style,
      }}
    >
      <Toolbar>
        <Typography className={classes.title} variant="h3" style={{ flexGrow: 1 }}>
          {selectedTranscript && pathname.includes('/web/conversations') && (
            <ArrowBackIosIcon className={classes.arrowBack} onClick={onCloseSelectedTranscript} />
          )}
          {getHeadingText()}

          {displayable.dev && (
            <Hidden xsDown>
              <BackendBadge
                style={{
                  marginLeft: 12,
                  backgroundColor: backend.backgroundColor,
                  color: 'white',
                  fontSize: 14,
                  lineHeight: '24px',
                }}
              >
                {backend.name}
                {features.ok} {displayable.dev}
              </BackendBadge>
            </Hidden>
          )}
        </Typography>
        <ProfileMenu />
      </Toolbar>
    </AppBar>
  );
};

const mapStateToProps = ({
  rateConversation: { conversationStatus, isOpen },
  savedTranscript: { transcripts, selectedId },
  scribeSession: { backends, captioner, currentPlatform, displayable, features },
}) => ({
  backends,
  conversationStatus,
  currentPlatform,
  displayable,
  features,
  isRateConversationOpen: isOpen,
  selectedTranscriptId: selectedId,
  transcripts,
  user: captioner,
});

const mapDispatchToProps = {
  clearConversationStatus,
  fetchAvaProfileMetrics,
  handleNewRoomStatus,
  setRateConversationOpen,
  setSelectedTranscriptId,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(HomeView));
